import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
    CalendarMonth, 
    AccessTime, 
    BuildRounded, 
    Delete, 
    ExpandMore,
    Add 
} from "@mui/icons-material";
import { 
    Accordion, 
    AccordionSummary, 
    AccordionDetails, 
    Typography,
    IconButton,
    Stack
} from "@mui/material";
import { MuiButton } from "@ace/ace-ui-web";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchSkills, Skills } from "../../redux/SkillReducer/skillSlice";
import { fetchSiteDetails } from "../../redux/SiteDetailsReducer/siteDetailsSlice";
import { submitGigShiftRequest } from "../../redux/GigRequestShiftReducer/gigRequestShiftSlice";
import { fetchGigRequests } from "../../redux/GigRequestsReducer/gigRequestsSlice";
import { GigShiftRequest } from "../../types/gigApi";
import { getShortTimeZoneLabelForDate } from "../../utils/timezoneUtils";
import { formatTimeRangeDisplay } from "../../utils/gigShiftUtils";
import { SelectorField, DateTimeField } from "./FormField";
import { ConfirmationDialog } from "../post-shift/ConfirmationDialog";
import styles from "./gigRequest.module.css";

interface DayEntry {
    id: string;
    date: string;
    skill: string[];
    startTime: string;
    endTime: string;
    notes: string;
}

interface DayErrors {
    date?: string;
    skill?: string;
    startTime?: string;
    endTime?: string;
}

interface GigRequestProps {
    setFooterContent?: (content: React.ReactNode | null) => void;
}

const DAY_NOTES_MAX_LENGTH = 100;
/** Statuses that still "hold" a date — a new request for that date must be blocked. */
const ACTIVE_GIG_STATUSES = new Set(['APP', 'PND', 'PST']);
const shouldSortDayEntries = (field: keyof DayEntry) => field === 'date' || field === 'startTime';

const compareRequiredValue = (aValue: string, bValue: string) => {
    if (aValue === bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    return aValue.localeCompare(bValue);
};

const compareDayEntries = (a: DayEntry, b: DayEntry) =>
    compareRequiredValue(a.date, b.date) ||
    compareRequiredValue(a.startTime, b.startTime);

/** Normalize a date-time or date string down to "yyyy-MM-dd" for comparison. */
const toDateOnly = (value: string | null | undefined): string => {
    if (!value) return "";
    return value.slice(0, 10);
};

const GigRequest: React.FC<GigRequestProps> = ({ setFooterContent }) => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { skills, loading: skillsLoading } = useAppSelector((state) => state.skills);
    const { siteDetails } = useAppSelector((state) => state.siteDetails);
    const { details: existingGigRequests } = useAppSelector((state) => state.gigRequests);

    const timeZone = siteDetails?.timeZoneId;
    const timeZoneShort = getShortTimeZoneLabelForDate(timeZone);

    const [dayEntries, setDayEntries] = useState<DayEntry[]>([]);
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
    const [errors, setErrors] = useState<Record<string, DayErrors>>({});
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch skills, site details, and this worker's existing gig requests on mount.
    // The existing-requests fetch lets us block duplicate-day submissions even if
    // the worker logged out/in (or switched devices) since their last request.
    useEffect(() => {
        if (currentUser?.siteCd) {
            dispatch(fetchSkills(currentUser.siteCd));
            dispatch(fetchSiteDetails(currentUser.siteCd));
        }
        if (currentUser?.siteCd && currentUser?.workdayId) {
            dispatch(fetchGigRequests({
                siteCd: currentUser.siteCd,
                userId: currentUser.workdayId,
                statusFilter: 'ALL',
            }));
        }
    }, [dispatch, currentUser?.siteCd, currentUser?.workdayId]);

    // Dates the current worker already has an active (non-cancelled/denied) gig request for.
    const existingRequestDates = useMemo(() => {
        const dates = new Set<string>();
        existingGigRequests.forEach((detail) => {
            const status = detail.requestStatus ?? '';
            if (ACTIVE_GIG_STATUSES.has(status)) {
                const dateOnly = toDateOnly(detail.requestDt);
                if (dateOnly) dates.add(dateOnly);
            }
        });
        return dates;
    }, [existingGigRequests]);

    // Skill options for dropdown
    const skillOptions = skills?.map((skill: Skills) => ({
        label: skill.lookupValueDesc,
        value: skill.lookupValueCd
    })) || [];

    const handleAddDay = () => {
        const newDay: DayEntry = {
            id: `day-${Date.now()}`,
            date: "",
            skill: [],
            startTime: "",
            endTime: "",
            notes: ""
        };
        setDayEntries([...dayEntries, newDay]);
        setExpandedPanel(newDay.id);
    };

    const handleRemoveDay = (id: string) => {
        setDayEntries(dayEntries.filter(day => day.id !== id));
    };

    const handleDayChange = (id: string, field: keyof DayEntry, value: string | string[]) => {
        const updated = dayEntries.map(day =>
            day.id === id ? { ...day, [field]: value } : day
        );

        if (shouldSortDayEntries(field)) {
            updated.sort(compareDayEntries);
        }

        setDayEntries(updated);

        // Clear error for this field when user starts typing
        setErrors(prev => {
            const next = { ...prev };
            if (next[id]) {
                const dayErrors = { ...next[id] };
                delete dayErrors[field as keyof DayErrors];
                if (Object.keys(dayErrors).length === 0) {
                    delete next[id];
                } else {
                    next[id] = dayErrors;
                }
            }
            return next;
        });
    };

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const formatDayLabel = (day: DayEntry) => {
        if (!day.date) return "";
        const dateObj = new Date(day.date + "T00:00:00");
        const formattedDate = dateObj.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        return formattedDate;
    };

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const validateDayEntry = useCallback((day: DayEntry): DayErrors => {
        const dayErrors: DayErrors = {};

        if (!day.date) {
            dayErrors.date = "Date is required";
        } else if (day.date < getTodayDate()) {
            dayErrors.date = "Date cannot be in the past";
        } else if (existingRequestDates.has(day.date)) {
            dayErrors.date = "You already have a gig request for this date";
        }

        if (!day.skill || day.skill.length === 0) {
            dayErrors.skill = "At least one skill is required";
        }

        if (!day.startTime) {
            dayErrors.startTime = "Start time is required";
        }

        if (!day.endTime) {
            dayErrors.endTime = "End time is required";
        }

        if (day.startTime && day.endTime && day.endTime === day.startTime) {
            dayErrors.endTime = "End time must be different from start time";
        }

        return dayErrors;
    }, [existingRequestDates]);

    const validateAllEntries = useCallback((): { isValid: boolean; firstErrorIndex?: number } => {
        const newErrors: Record<string, DayErrors> = {};
        let hasErrors = false;
        let firstErrorDayId: string | null = null;
        let firstErrorIndex: number | undefined = undefined;

        // Also guard against duplicate dates entered within the same form submission.
        const seenDates = new Set<string>();

        dayEntries.forEach((day, index) => {
            const dayErrors = validateDayEntry(day);
            if (day.date && !dayErrors.date) {
                if (seenDates.has(day.date)) {
                    dayErrors.date = "Duplicate date — each day in this request must be unique";
                } else {
                    seenDates.add(day.date);
                }
            }
            if (Object.keys(dayErrors).length > 0) {
                newErrors[day.id] = dayErrors;
                hasErrors = true;
                if (!firstErrorDayId) {
                    firstErrorDayId = day.id;
                    firstErrorIndex = index;
                }
            }
        });

        setErrors(newErrors);

        if (firstErrorDayId) {
            setExpandedPanel(firstErrorDayId);
        }

        return { isValid: !hasErrors, firstErrorIndex };
    }, [dayEntries, validateDayEntry]);

    const handleSubmit = useCallback(() => {
        if (dayEntries.length === 0) {
            toast.error("Please add at least one day entry");
            return;
        }

        const validation = validateAllEntries();
        if (!validation.isValid) {
            const dayNumber = validation.firstErrorIndex !== undefined ? validation.firstErrorIndex + 1 : '';
            toast.error(`Please fill in all required fields${dayNumber ? ` (check Day ${dayNumber})` : ''}`);
            return;
        }

        setShowConfirmDialog(true);
    }, [dayEntries.length, validateAllEntries]);

    const resetForm = () => {
        setDayEntries([]);
        setExpandedPanel(false);
        setErrors({});
    };

    const skillLabel = (code: string) =>
        skillOptions.find((s) => s.value === code)?.label || code;

    const confirmSubmit = async () => {
        if (isSubmitting) return;
        if (!currentUser?.workdayId || !currentUser?.siteCd) {
            toast.error("Missing user information. Please refresh and try again.");
            return;
        }
        setIsSubmitting(true);
        try {
            const payload: GigShiftRequest = {
                siteCd: currentUser.siteCd,
                userName: `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim(),
                workdayId: currentUser.workdayId,
                requestNotes: dayEntries.find((d) => d.notes)?.notes || null,
                requestSchedule: dayEntries.map((day) => ({
                    requestDate: day.date,
                    startTime: day.startTime,
                    endTime: day.endTime,
                    skillCd: day.skill,
                    requestNotes: day.notes || null,
                })),
            };

            await dispatch(submitGigShiftRequest(payload)).unwrap();

            toast.success("Gig request submitted successfully!");
            setShowConfirmDialog(false);
            resetForm();

            // Refresh the "already requested" dates so a subsequent add-day in
            // the same session immediately reflects the new submission.
            if (currentUser?.siteCd && currentUser?.workdayId) {
                dispatch(fetchGigRequests({
                    siteCd: currentUser.siteCd,
                    userId: currentUser.workdayId,
                    statusFilter: 'ALL',
                }));
            }
        } catch (err: any) {
            console.error("Gig request submission error:", err);
            const message = err?.message || "Failed to submit gig request. Please try again.";
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footerActions = useMemo(() => dayEntries.length > 0 ? (
        <MuiButton
            onClick={handleSubmit}
            variant="contained"
            colors="primary"
            sx={{
                width: '100%',
                minWidth: { sm: '250px' },
                padding: '0.75rem 2rem',
                fontWeight: 600,
                backgroundColor: '#d40029',
                '&:hover': {
                    backgroundColor: '#a00021'
                }
            }}
        >
            Submit Request
        </MuiButton>
    ) : null, [dayEntries.length, handleSubmit]);

    useEffect(() => {
        if (!setFooterContent) return;
        setFooterContent(footerActions);
        return () => setFooterContent(null);
    }, [setFooterContent, footerActions]);

    return (
        <div className={styles.drawerContainer}>
            <div className={styles.contentWrapper}>
                <Stack spacing={2.5}>
                    <div className={styles.content}>
                        <AnimatePresence>
                                {dayEntries.map((day, index) => (
                                    <motion.div
                                        key={day.id}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Accordion
                                            expanded={expandedPanel === day.id}
                                            onChange={handleAccordionChange(day.id)}
                                            className={styles.accordion}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore />}
                                                className={styles.accordionSummary}
                                            >
                                                <div className={styles.accordionHeader}>
                                                    <Typography className={styles.dayNumber}>
                                                        Day {index + 1}
                                                    </Typography>
                                                    <Typography className={styles.dayLabel}>
                                                        {formatDayLabel(day)}
                                                    </Typography>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveDay(day.id);
                                                        }}
                                                        className={styles.deleteButton}
                                                        size="small"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </div>
                                            </AccordionSummary>
                                            <AccordionDetails className={styles.accordionDetails}>
                                                {/* Skill Row */}
                                                <div className={styles.skillShiftRow}>
                                                    <SelectorField
                                                        label="Skill"
                                                        icon={<BuildRounded className={styles.icon} />}
                                                        placeholder="Select Skill(s)"
                                                        value={day.skill}
                                                        onChange={(value) => handleDayChange(day.id, 'skill', value)}
                                                        options={skillOptions}
                                                        disabled={skillsLoading}
                                                        multipleSelect={true}
                                                        error={errors[day.id]?.skill}
                                                    />
                                                </div>

                                                {/* Date, Start Time, and End Time Row */}
                                                <div className={styles.dateHoursRow}>
                                                    <div className={`${styles.formGroup} form-group`}>
                                                        <div className={styles.labelWrapper}>
                                                            <CalendarMonth className={styles.icon} />
                                                            <p className={styles.label}>Date</p>
                                                        </div>
                                                        <input
                                                            type="date"
                                                            value={day.date}
                                                            min={getTodayDate()}
                                                            onChange={(e) => handleDayChange(day.id, 'date', e.target.value)}
                                                            required
                                                        />
                                                        {errors[day.id]?.date && <p className={styles.errorMessage}>{errors[day.id].date}</p>}
                                                    </div>

                                                    <DateTimeField
                                                        label={`Start Time (${timeZoneShort})`}
                                                        icon={<AccessTime className={styles.icon} />}
                                                        type="time"
                                                        value={day.startTime}
                                                        onChange={(e) => handleDayChange(day.id, 'startTime', e.target.value)}
                                                        error={errors[day.id]?.startTime}
                                                    />

                                                    <DateTimeField
                                                        label={`End Time (${timeZoneShort})`}
                                                        icon={<AccessTime className={styles.icon} />}
                                                        type="time"
                                                        value={day.endTime}
                                                        onChange={(e) => handleDayChange(day.id, 'endTime', e.target.value)}
                                                        error={errors[day.id]?.endTime}
                                                    />
                                                </div>

                                                {/* Notes Field */}
                                                <div className={styles.formGroup}>
                                                    <label className={styles.label}>Notes (Optional)</label>
                                                    <textarea
                                                        className={styles.textarea}
                                                        value={day.notes}
                                                        onChange={(e) => handleDayChange(day.id, 'notes', e.target.value)}
                                                        placeholder="Add any additional notes..."
                                                        maxLength={DAY_NOTES_MAX_LENGTH}
                                                        rows={3}
                                                    />
                                                    <p className={styles.charCounter}>{day.notes.length} / {DAY_NOTES_MAX_LENGTH}</p>
                                                </div>
                                            </AccordionDetails>
                                        </Accordion>
                                    </motion.div>
                                ))}
                            </AnimatePresence>

                            {/* Add Day Button */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.1 }}
                                style={{ display: 'flex', justifyContent: 'center' }}
                            >
                                <MuiButton
                                    onClick={handleAddDay}
                                    variant="outlined"
                                    colors="primary"
                                    sx={{
                                        marginTop: '1rem',
                                        border: '2px dashed #d40029',
                                        color: '#d40029',
                                        padding: '0.75rem 2rem',
                                        fontWeight: 600,
                                        '&:hover': {
                                            backgroundColor: 'rgba(212, 0, 41, 0.05)',
                                            borderColor: '#a00021'
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Add sx={{ fontSize: '1.25rem', transform: 'translateY(-1px)' }} />
                                        <span>Add Day</span>
                                    </Stack>
                                </MuiButton>
                            </motion.div>

                                    </div>
                                </Stack>
                            </div>

                                                <ConfirmationDialog
                                                    open={showConfirmDialog}
                                                    onClose={() => setShowConfirmDialog(false)}
                                                    onConfirm={confirmSubmit}
                                                    title="Confirm Gig Request"
                                                    description="Please review your gig request details before submitting"
                                                    confirmLabel="Submit Request"
                                                    isSubmitting={isSubmitting}
                                                >
                                                    <div className={styles.confirmDayList}>
                                                        {dayEntries.map((day, idx) => {
                                                            const timeRangeText = formatTimeRangeDisplay(day.startTime, day.endTime);
                                                            return (
                                                                <div key={day.id} className={styles.confirmDayCard}>
                                                                    <div className={styles.confirmDayHeader}>
                                                                        <span className={styles.confirmDayTitle}>Day {idx + 1}</span>
                                                                        <span className={styles.confirmDayDate}>{formatDayLabel(day) || 'No date'}</span>
                                                                    </div>
                                                                    <div className={styles.confirmDayGrid}>
                                                                        <div className={styles.confirmField}>
                                                                            <span className={styles.confirmFieldLabel}>Skill</span>
                                                                            <span className={styles.confirmFieldValue}>
                                                                                {day.skill.map(skillLabel).join(', ') || '—'}
                                                                            </span>
                                                                        </div>
                                                                        <div className={styles.confirmField}>
                                                                            <span className={styles.confirmFieldLabel}>Time</span>
                                                                            <span className={styles.confirmFieldValue}>{timeRangeText || '—'}</span>
                                                                        </div>
                                                                        {day.notes && (
                                                                            <div className={`${styles.confirmField} ${styles.confirmNotes}`}>
                                                                                <span className={styles.confirmFieldLabel}>Notes</span>
                                                                                <span className={styles.confirmFieldValue}>{day.notes}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </ConfirmationDialog>
                                            </div>
                                        );
                                    };

                                    export default GigRequest;
